import { useInfiniteQuery } from '@tanstack/react-query';
import useSlingApi from '@sdk/api/useSlingApi';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';

import { getArticlesQueryKey } from '@web/newsfeed/utils/getQueryKey';
import { NEWSFEED_PAGE_CACHE_TIME } from '@web/newsfeed/consts/cache';

import { useNewsfeedMappers } from './useNewsfeedMappers';

const ARTICLES_PAGE_SIZE = 20;

type Props = {
  pageId: ID;
};

export function useGetArticles({ pageId }: Props) {
  const api = useSlingApi();
  const { showErrorToast } = useToast();
  const { isReady: isMapperReady, mapAPIArticleToUI } = useNewsfeedMappers();
  const queryKey = getArticlesQueryKey(pageId);

  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const lastArticleId: ID = pageParam?.lastArticleId;
      const { data } = await api.newsfeed.getArticles({ pageId, lastArticleId });

      return data.map((article) => mapAPIArticleToUI(article));
    },
    onError: (error) => {
      showErrorToast(getErrorForToast(error));
    },
    cacheTime: NEWSFEED_PAGE_CACHE_TIME,
    enabled: isMapperReady,
    getNextPageParam: (lastPage) => {
      const hasOlderArticles = lastPage.length >= ARTICLES_PAGE_SIZE;
      if (!hasOlderArticles) {
        return;
      }

      const lastItem = lastPage[lastPage.length - 1];
      return { lastArticleId: lastItem.id };
    },
  });
}
